import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentIllustration,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetHeaderCloseButton,
} from '@/components/ModalSheet'
import { JobClosedNotificationBar } from '@/features/admin/scope-change-requests/components/ScopeChangeRequestDetailsPanel/DetailsFooter'
import { Button } from '@/components/Button'
import { ToggleGroup, ToggleItem } from '@/components/ToggleGroup'
import styles from './ScopeChangeRequestResolveDialog.module.css'
import { byDesign } from '@/infra/design'
import imgSendPromptMessageUrl from '@/assets/misc/img_send-prompt-message.svg'
import TeamRolesIllustration from '@/components/SpotIllustration/TeamRolesIllustration'
import { type ScopeChangeRequestResolveAction } from './ScopeChangeRequestResolveDialogViewModel'

export interface ScopeChangeRequestResolveDialogProps {
  showing: boolean
  onClose: () => unknown
  corporateMode: boolean
  clientName?: string
  resolveAction: ScopeChangeRequestResolveAction
  onActionChange: (action: ScopeChangeRequestResolveAction) => void
  isJobClosed: boolean
  onResolve: () => unknown
  isResolvePending: boolean
}

export function ScopeChangeRequestResolveDialog({
  showing,
  onClose,
  corporateMode,
  clientName,
  resolveAction,
  onActionChange,
  isJobClosed,
  onResolve,
  isResolvePending,
}: ScopeChangeRequestResolveDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isResolvePending ? undefined : onClose}
    >
      <ModalSheetHeader>
        <div className={styles.closeButtonContainer}>
          <ModalSheetHeaderCloseButton testId="scope-change-request-close-resolve-dialog" />
        </div>
      </ModalSheetHeader>
      <ModalSheetContent>
        <ModalSheetContentBlock>
          {!corporateMode && (
            <ModalSheetContentIllustration
              src={imgSendPromptMessageUrl}
              illustration={byDesign(undefined, TeamRolesIllustration)}
              alt="Resolve"
            />
          )}
          <ModalSheetContentTitle>
            Resolve job scope discrepancy
          </ModalSheetContentTitle>
          <ModalSheetContentDescription>
            Discuss resolutions with client{' '}
            <span className={styles.collaboratorName}>{clientName}</span>, and
            select the agreed upon action to resolve this change request.
          </ModalSheetContentDescription>
          <ToggleGroup stretch>
            <ToggleItem
              toggled={resolveAction === 'Accept'}
              onChange={() => onActionChange('Accept')}
              isDisabled={isJobClosed}
              testId="scope-change-request-accept"
            >
              Accept on behalf of the client
            </ToggleItem>
            <ToggleItem
              toggled={resolveAction === 'Reject'}
              onChange={() => onActionChange('Reject')}
              testId="scope-change-request-reject"
            >
              Reject on behalf of the client
            </ToggleItem>
          </ToggleGroup>
        </ModalSheetContentBlock>
      </ModalSheetContent>
      <JobClosedNotificationBar showing={isJobClosed} />
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            variant="default"
            isDisabled={isResolvePending}
            onPress={onClose}
          />
          <Button
            label="Resolve"
            onPress={onResolve}
            variant="primary"
            isDisabled={resolveAction === 'None' || isResolvePending}
            testId="scope-change-request-resolve-client"
            isPending={isResolvePending}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}
